/**
 * Which highlight cards a memorial page shows, and in what order. The page is cached, so
 * the phase and whether flowers are still open are worked out here, in the browser.
 * Pure: the caller passes today's date and the slideshow facts (`hasSlideshow()`,
 * `lifeStoryOnPage()` from useSlideshow.ts).
 */

/** In calendar days to the ceremony: 3+ before, 1–2 before, the day, 1–364 after, 365+ after. */
export const PHASES = [
  "before",
  "final_days",
  "ceremony_day",
  "first_year",
  "after_first_year",
] as const;

export type Phase = (typeof PHASES)[number];

/** What tracking reports: a page without a ceremony date is kept apart. */
export type TrackedPhase = Phase | "no_date";

export type CardId =
  | "flowers"
  | "family_flowers"
  | "photos"
  | "slideshow"
  | "life_story"
  | "memorial_gift"
  | "memory_book"
  | "book_upcoming"
  | "candle";

export interface Candle {
  image: string;
  price: string;
  url: string;
}

/** A photo from the Minnessidor options, shown instead of the card's fact box. */
export interface CardPhoto {
  alt: string;
  src: string;
}

/** `data-highlights` on `#obituary-current-events__vue` (ScheduledViewsTransformer::highlights). */
export interface Highlights {
  candle: Candle | null;
  /** ISO 8601, or '' without a date. */
  ceremonyDate: string;
  donations: boolean;
  familyFlowersUrl: string;
  /** ISO 8601, or '' where our shop takes no orders for this page. */
  flowersDeadline: string;
  greetings: boolean;
  /** Keyed by card; a card without one keeps its fact box. */
  images: Partial<Record<CardId, CardPhoto>>;
  /** Ask the API for the family's memory book (the Swedish and Danish sites). */
  memoryBook: boolean;
  /** IANA zone the ceremony's calendar days count in, or '' for the visitor's. */
  timeZone: string;
}

export interface CardsState {
  /** The API sells the family's memory book to the page's visitors (memory-book.ts). */
  bookForSale?: boolean;
  /** Nothing is for sale yet, but the schedule will sell the book on this page (memory-book.ts). */
  bookUpcoming?: boolean;
  /** `hasSlideshow()`: at least MIN_PHOTOS photos and films. */
  hasSlideshow: boolean;
  highlights: Highlights;
  /** `lifeStoryOnPage()`: 6 dated photos across 3 decades, timeline on. */
  lifeStory: boolean;
  now: Date;
}

export const MAX_CARDS = 5;

/**
 * Whether the memorial candle card is shown in "Aktuellt". Hidden for now since
 * hardly any candles sell this way (PRO-4690). The candle product, shop and
 * highlights data stay in place: set to true to show the card again.
 */
export const CANDLE_CARD_ENABLED = false;

/** Score per phase, in PHASES order; null = not shown in that phase. */
export const SCORES: Record<CardId, (number | null)[]> = {
  // In the photos card's place while the book is on its way: the same ask, with the book as the reason.
  book_upcoming: [50, 50, 45, 55, 40],
  candle: [30, 35, 40, 80, 90],
  // Before the ceremony only once our shop has closed (see eligible), never beside flowers.
  // After it, above the gift: few give a gift once the ceremony is over.
  family_flowers: [60, 60, 55, 70, 45],
  flowers: [70, 95, null, null, null],
  life_story: [60, 65, 80, 100, 100],
  memorial_gift: [65, 70, 85, 65, 35],
  // The book is made from the page's greetings and photos, so it grows into the first year.
  // In that year it leads, ahead of the life story: it is the news, the life story is always
  // there. After it the life story leads again.
  memory_book: [55, 60, 70, 105, 75],
  photos: [50, 50, 45, 55, 40],
  slideshow: [55, 60, 75, 85, 80],
};

const DAY_MS = 86_400_000;

/** The calendar date in a time zone, as a UTC midnight timestamp. */
function calendarDay(d: Date, timeZone?: string): number {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-CA", {
      day: "numeric",
      month: "numeric",
      timeZone: timeZone || undefined,
      year: "numeric",
    }).formatToParts(d);
  } catch {
    // An unknown zone name: fall back to the visitor's.
    return calendarDay(d);
  }
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day"));
}

/** Calendar days from today to the ceremony, in the site's time zone (the visitor's without one). */
function daysToCeremony(ceremony: Date, now: Date, timeZone?: string): number {
  return (
    (calendarDay(ceremony, timeZone) - calendarDay(now, timeZone)) / DAY_MS
  );
}

export function pagePhase(
  ceremonyDate: string,
  now: Date,
  timeZone?: string
): TrackedPhase {
  const ceremony = new Date(ceremonyDate);
  if (!ceremonyDate || Number.isNaN(ceremony.getTime())) {
    return "no_date";
  }
  const days = daysToCeremony(ceremony, now, timeZone);
  if (days > 2) {
    return "before";
  }
  if (days > 0) {
    return "final_days";
  }
  if (days === 0) {
    return "ceremony_day";
  }
  return days > -365 ? "first_year" : "after_first_year";
}

/** No date counts as after the ceremony. */
export function selectionPhase(phase: TrackedPhase): Phase {
  return phase === "no_date" ? "first_year" : phase;
}

/** Our shop still takes orders for the ceremony. */
export function flowersOpen(flowersDeadline: string, now: Date): boolean {
  return Date.parse(flowersDeadline) > now.getTime();
}

function bookOnItsWay(state: CardsState): boolean {
  return (
    state.highlights.memoryBook && !!state.bookUpcoming && !state.bookForSale
  );
}

function eligible(id: CardId, state: CardsState): boolean {
  const { highlights: h, now } = state;
  switch (id) {
    case "flowers":
      return flowersOpen(h.flowersDeadline, now);
    case "family_flowers":
      return !!h.familyFlowersUrl && !flowersOpen(h.flowersDeadline, now);
    case "photos":
      return h.greetings && !bookOnItsWay(state);
    case "book_upcoming":
      return h.greetings && bookOnItsWay(state);
    case "slideshow":
      return h.greetings && state.hasSlideshow && !state.lifeStory;
    case "life_story":
      return h.greetings && state.lifeStory;
    case "memorial_gift":
      return h.donations;
    case "memory_book":
      return h.memoryBook && !!state.bookForSale;
    case "candle":
      return CANDLE_CARD_ENABLED && !!h.candle?.url;
    default:
      return false;
  }
}

/** The cards to show, highest score first, at most MAX_CARDS. */
export function currentCards(state: CardsState): CardId[] {
  const column = PHASES.indexOf(
    selectionPhase(
      pagePhase(
        state.highlights.ceremonyDate,
        state.now,
        state.highlights.timeZone
      )
    )
  );
  return (Object.keys(SCORES) as CardId[])
    .map((id) => ({ id, score: SCORES[id][column] }))
    .filter(
      (card): card is { id: CardId; score: number } =>
        card.score !== null && eligible(card.id, state)
    )
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_CARDS)
    .map((card) => card.id);
}

/** `data-highlights`, with safe defaults for a missing or broken attribute. */
export function parseHighlights(raw: string | undefined): Highlights {
  let data: Partial<Highlights> = {};
  try {
    data = JSON.parse(raw ?? "") ?? {};
  } catch {
    // Defaults below.
  }
  const { candle } = data;
  return {
    candle:
      candle && typeof candle.url === "string" && candle.url
        ? {
            image: String(candle.image ?? ""),
            price: String(candle.price ?? ""),
            url: candle.url,
          }
        : null,
    ceremonyDate: String(data.ceremonyDate ?? ""),
    donations: data.donations === true,
    familyFlowersUrl: String(data.familyFlowersUrl ?? ""),
    flowersDeadline: String(data.flowersDeadline ?? ""),
    greetings: data.greetings === true,
    images: parseImages(data.images),
    memoryBook: data.memoryBook === true,
    timeZone: String(data.timeZone ?? ""),
  };
}

/** PHP encodes an empty options list as `[]`, so anything but an object is none. */
function parseImages(raw: unknown): Partial<Record<CardId, CardPhoto>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const images: Partial<Record<CardId, CardPhoto>> = {};
  for (const [card, image] of Object.entries(
    raw as Record<string, Partial<CardPhoto> | null>
  )) {
    if (image && typeof image.src === "string" && image.src) {
      images[card as CardId] = { alt: String(image.alt ?? ""), src: image.src };
    }
  }
  return images;
}
